import styled from "@emotion/styled";
import { Form, Image } from "react-bootstrap";
import { IStyledComponentProps } from "../../theme/theme.types";

const FormContainer = styled.div<IStyledComponentProps>`
  &.form-container {
    height: 100%;
    padding: 80px 20px 20px;

    &.search-results {
      padding: 80px 20px 20px 6px;
    }

    .ce-soar-button {
      &.submit-outage-button,
      &.back-button {
        width: 100%;
        margin-bottom: 12px;
      }

      &.submit-outage-button {
        .loading-spinner {
          display: flex;
          width: 100%;
          justify-content: center;
          .loading-message {
            align-self: center;
            display: inline-block;
            text-align: center;
            padding-left: 8px;
          }
        }
      }
    }

    &.force-scroll {
      overflow-y: scroll;
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      padding-bottom: 100px;
    }
  }
`;

const FormGroup = styled(Form.Group)<IStyledComponentProps>`
  &.form-group {
    padding-bottom: 0;
    margin-bottom: 28px;

    &.file-upload-form-group {
      .upload-message {
        font-size: ${(props) =>
          props.theme?.utils?.getFontThemeSize("smallest")};
        margin-top: 4px;
        margin-bottom: 0;
      }

      .link-button {
        color: ${(props) => props.theme.colors.alerts.secondary};
        font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
        text-decoration: underline;
        padding-left: 0;
        margin-bottom: 12px;
        svg {
          margin-right: 6px;
        }
      }

      .image-container {
        width: 100%;
        img {
          padding: 12px;
          width: 100%;
        }

        .image-controls {
          display: flex;
          div {
            flex: 1;
            margin-right: 8px;
          }

          button {
            background-color: ${(props) => props.theme.colors.essential.white};
            flex: 1;
            padding: 10px !important;
            font-size: ${(props) =>
              props.theme.utils.getFontThemeSize("small")};

            &:hover {
              cursor: pointer;
              transform: scale(1);
            }

            &.replace-image {
              color: ${(props) => props.theme.colors.brand.primary};
              border-color: ${(props) => props.theme.colors.brand.primary};
              margin-right: 8px;
              &:hover {
                color: ${(props) =>
                  props.theme.colors.brand.secondary}!important;
                border-color: ${(props) =>
                  props.theme.colors.brand.secondary}!important;
              }
            }
            &.remove-image {
              color: ${(props) => props.theme.colors.essential.white};
              background-color: ${(props) => props.theme.colors.alerts.primary};
              border-color: ${(props) => props.theme.colors.alerts.primary};
            }
          }
        }
      }

      .processing-image-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        align-items: center;
        .spinner-border {
          display: block;
        }
      }

      .file-upload-control {
        span.file-upload-types {
          color: ${(props) => props.theme?.colors.grays.septenary};
          font-size: ${(props) =>
            props.theme?.utils?.getFontThemeSize("smallest")};
        }
      }
    }
  }
`;

const FormControl = styled(Form.Control)<IStyledComponentProps>`
  border-radius: 0;
  height: 60px;
  &.form-control {
    font-family: ${(props) => props.theme?.fontFamilies?.regular};

    &.outage-form-dropdown {
      -moz-appearance: none;
      .select-placeholder {
        color: ${(props) => props.theme?.colors.grays.septenary};
        opacity: 80%;
        font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
      }
      &.is-invalid,
      &.is-valid {
        background-position: right 1.5em center;
      }
    }

    &.search-input {
      margin-top: 12px;
    }

    &.form-text-area {
      height: 120px;
      padding-right: 3.25em;
      &.is-invalid,
      &.is-valid {
        background-position: right 1.5em top 0.5em;
      }
    }

    &.form-counter {
      color: ${(props) => props.theme?.colors.grays.septenary};
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("smallest")};
      text-align: right;
      width: 100%;
      display: inline-block;
    }

    &.form-phone-number,
    .form-email {
      margin-top: 6px;
    }
  }
`;

const FormLabel = styled(Form.Label)<IStyledComponentProps>`
  &.form-label {
    display: block;
    font-family: ${(props) => props.theme?.fontFamilies?.semiBold};
    font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
    &:first-child {
      margin-bottom: 0;
    }
    &.optional {
      color: ${(props) => props.theme?.colors.grays.septenary};
    }
    &.file-upload-label {
      background-color: ${(props) => props.theme.colors.essential.white};
      color: ${(props) => props.theme.colors.brand.primary};
      border: 1px solid ${(props) => props.theme.colors.brand.primary};
      font-family: Open Sans Semi Bold;
      padding: 12px 20px;
      text-align: center;
      &:hover {
        color: ${(props) => props.theme.colors.brand.secondary}!important;
        border: 1px solid
          ${(props) => props.theme.colors.brand.secondary}!important;
        cursor: pointer;
      }
    }
  }
`;

const FormHeading = styled.div<IStyledComponentProps>`
  &.form-heading {
    h1 {
      font-family: ${(props) => props.theme?.fontFamilies?.semiBold};
      font-size: ${(props) => props.theme?.utils?.increaseFontSize(2)};
    }

    p {
      font-family: ${(props) => props.theme?.fontFamilies?.regular};
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
    }
  }
`;

const FormSubmitContainer = styled.div<IStyledComponentProps>`
  &.form-submit-container {
    margin-top: 3em;

    button.search-outage-button {
      width: 100%;
    }
  }
`;

const FormConfirmationContainer = styled.div<IStyledComponentProps>`
  &.form-confirmation-container {
    height: 100%;
    overflow-y: scroll;
    padding: 60px 20px 20px;

    img.submission-image {
      margin-bottom: 26px;
      width: 100%;
    }

    .ce-soar-button {
      &.back-button {
        width: 100%;
        margin-bottom: 12px;
      }
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      padding-bottom: 100px;
    }
  }
`;

const ConfirmationIcon = styled(Image)<IStyledComponentProps>`
  &.confirmation-icon {
    display: block;
    height: 100px;
    margin: 10px auto;
    width: 100px;
  }
`;

const ConfirmationHeading = styled.div<IStyledComponentProps>`
  &.confirmation-heading {
    h1 {
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("large")};
    }
    p {
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
    }
  }
`;

const ConfirmationReport = styled.div<IStyledComponentProps>`
  &.confirmation-report {
    h2 {
      font-family: ${(props) => props.theme?.fontFamilies?.semiBold};
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("base")};
    }
    .confirmation-report-container {
      font-size: ${(props) => props.theme?.utils?.getFontThemeSize("small")};
      margin-bottom: 20px;
      .row {
        margin: 4px -15px;
      }
      .label-column {
        padding-left: 0;
        padding-right: 0;
      }
      .wrapped-column {
        b {
          font-family: ${(props) => props.theme?.fontFamilies?.semiBold};
        }
      }
    }
  }
`;

export default {
  ConfirmationHeading,
  ConfirmationReport,
  ConfirmationIcon,
  FormContainer,
  FormSubmitContainer,
  FormConfirmationContainer,
  FormGroup,
  FormControl,
  FormLabel,
  FormHeading,
};
